import { ArrowRight, Phone, Home as HomeIcon, ChevronRight, Calendar, Clock, Tag, User } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";
import BackToTop from "../../components/BackToTop";
import { Button } from "../../components/ui/button";
import { useSEO } from "../../lib/useSEO";
import { SITE } from "../../lib/site";
import { POSTS } from "../../lib/blog";

export default function BlogPostLayout({ post, children, howTo, faq }) {
  const canonical = `https://kreuse-exterieur.fr/conseils/${post.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Article", "BlogPosting"],
        "@id": `${canonical}#article`,
        headline: post.title,
        description: post.seoDescription || post.excerpt,
        datePublished: post.date,
        dateModified: post.date,
        image: post.image?.startsWith("http") ? post.image : `https://kreuse-exterieur.fr${post.image}`,
        author: {
          "@type": "Person",
          name: "Kevin",
          jobTitle: "Paysagiste & applicateur biocide certifié Certibiocide",
          worksFor: {
            "@type": "LocalBusiness",
            "@id": "https://kreuse-exterieur.fr/#business",
            name: "K'REUSE EXTÉRIEUR",
          },
        },
        publisher: {
          "@type": "Organization",
          name: "K'REUSE EXTÉRIEUR",
          logo: {
            "@type": "ImageObject",
            url: `https://kreuse-exterieur.fr${SITE.logo}`,
          },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        keywords: post.keywords?.join(", "),
        articleSection: post.category,
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "h2", ".article-lead"],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: "Conseils & actualités", item: "https://kreuse-exterieur.fr/conseils" },
          { "@type": "ListItem", position: 3, name: post.title, item: canonical },
        ],
      },
      ...(howTo
        ? [{
            "@type": "HowTo",
            name: howTo.name || post.title,
            description: howTo.description || post.excerpt,
            image: post.image,
            totalTime: howTo.totalTime,
            step: howTo.steps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.name,
              text: s.text,
            })),
          }]
        : []),
      ...(faq
        ? [{
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  useSEO({
    title: post.seoTitle || `${post.title} | K'REUSE`,
    description: post.seoDescription || post.excerpt,
    canonical,
    jsonLd,
  });

  const others = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);
  const dateFmt = new Date(post.date).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E8F5E9] opacity-70 blur-3xl" />
          </div>
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <Link to="/conseils" className="hover:text-[#1B5E20]">Conseils</Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium truncate">{post.title}</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8F5E9] text-[#1B5E20] text-xs font-semibold uppercase tracking-wider mb-5">
              <Tag className="w-3.5 h-3.5" />
              {post.category}
            </div>

            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A2E1C] leading-[1.1]">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-[#5C705E]">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {dateFmt}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readMin} min de lecture
              </span>
              <span className="flex items-center gap-1.5" data-testid="post-author">
                <User className="w-4 h-4" />
                Par Kevin, K'REUSE EXTÉRIEUR
              </span>
            </div>
          </div>
        </section>

        {/* Cover image */}
        <section className="pb-12">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="relative aspect-[16/9] rounded-[2rem] overflow-hidden shadow-xl">
              <img
                src={post.image}
                alt={post.title}
                width="1600"
                height="1067"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </section>

        {/* Article body */}
        <article className="pb-8">
          <div className="max-w-3xl mx-auto px-6 md:px-10 prose-kreuse">
            {children}
            {faq && (
              <section data-testid="post-faq">
                <h2>Questions fréquentes</h2>
                {faq.map((f) => (
                  <div key={f.q}>
                    <h3>{f.q}</h3>
                    <p>{f.a}</p>
                  </div>
                ))}
              </section>
            )}
          </div>
        </article>

        {/* Author bio — E-E-A-T signal */}
        <section className="pb-20">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <div className="bg-[#F8FBF8] border border-[#E5EBE5] rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-start gap-5" data-testid="author-bio">
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-[#1B5E20] to-[#2e7d32] flex items-center justify-center text-white font-['Outfit'] text-2xl font-medium">
                K
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold mb-1">
                  À propos de l'auteur
                </div>
                <h3 className="font-['Outfit'] font-semibold text-lg text-[#1A2E1C] mb-2">
                  Kevin — Fondateur K'REUSE EXTÉRIEUR
                </h3>
                <p className="text-sm text-[#5C705E] leading-relaxed">
                  Paysagiste certifié <strong>Certibiocide</strong> et fondateur de K'REUSE EXTÉRIEUR à
                  La Celle-Dunoise (23800). Kevin intervient dans toute la Creuse pour les particuliers et
                  professionnels : création et entretien de jardin, élagage, lutte anti-nuisibles (frelons,
                  punaises de lit, dératisation). 25 avis Google 5,0/5.
                </p>
                <Link
                  to="/a-propos-kevin-fondateur"
                  data-testid="author-bio-link"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-[#1B5E20] hover:text-[#154018] font-medium"
                >
                  En savoir plus sur Kevin
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-[#1B5E20] to-[#2e7d32] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light tracking-tight">
              Besoin d'un professionnel en Creuse&nbsp;?
            </h2>
            <p className="mt-4 text-white/85">
              Devis gratuit sous 48h. On intervient sur toute la Creuse (23) et alentours.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 justify-center">
              <Button asChild className="bg-white text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-6 py-6 font-medium">
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button asChild className="bg-[#0F2E11] hover:bg-black text-white rounded-full px-6 py-6 font-medium">
                <a href="/#contact">
                  Demander un devis
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="py-16 bg-white/40">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-8">
              <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                À lire aussi
              </div>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2E1C]">
                Autres <span className="font-medium text-[#1B5E20]">conseils</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/conseils/${p.slug}`}
                  className="group bg-white border border-[#E5EBE5] rounded-2xl overflow-hidden hover:shadow-md transition-all"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      width="1600"
                      height="1067"
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold mb-2">
                      {p.category}
                    </div>
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] leading-snug group-hover:text-[#1B5E20] transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
